import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { API_BASE } from "../../api.js";
import { fixImageUrl } from "../../utils/fixImageUrl.js";
import "./Search.css";

export default function Search() {
  const { t } = useTranslation();
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(false);
  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const debounceRef = useRef(null);

  useEffect(() => {
    if (active && inputRef.current) inputRef.current.focus();
  }, [active]);

  const doSearch = useCallback(async (q) => {
    if (!q || q.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/search`, { params: { q: q.trim() } });
      setResults(res.data || []);
      setOpen(true);
    } catch {
      setResults([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    clearTimeout(debounceRef.current);
    if (!query.trim()) {
      setResults([]);
      setOpen(false);
      return;
    }
    debounceRef.current = setTimeout(() => doSearch(query), 350);
    return () => clearTimeout(debounceRef.current);
  }, [query, doSearch]);

  // Close dropdown (and collapse) when clicking outside
  useEffect(() => {
    const handler = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setOpen(false);
        if (!query) setActive(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [query]);

  const handleSelect = (id) => {
    setQuery("");
    setOpen(false);
    window.location.href = `/ArtistProfile/${id}`;
  };

  const handleKeyDown = (e) => {
    if (e.key === "Escape") {
      setOpen(false);
      setQuery("");
      setActive(false);
    }
  };

  const MagnifierIcon = () => (
    <svg width="14" height="14" viewBox="0 0 24 24"
      fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );

  return (
    <div className={`spar-search${active ? " active" : ""}`} ref={containerRef}>
      {/* Collapsed: icon-only button */}
      {!active && (
        <button
          className="spar-search-toggle"
          onClick={() => setActive(true)}
          aria-label={t("search.placeholder")}
        >
          <MagnifierIcon />
        </button>
      )}

      {/* Expanded: full search box */}
      {active && (
        <div className="spar-search-box">
          <svg className="spar-search-icon" width="14" height="14" viewBox="0 0 24 24"
            fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <circle cx="11" cy="11" r="8" />
            <line x1="21" y1="21" x2="16.65" y2="16.65" />
          </svg>
          <input
            ref={inputRef}
            type="text"
            className="spar-search-input"
            placeholder={t("search.placeholder")}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onFocus={() => results.length > 0 && setOpen(true)}
            onKeyDown={handleKeyDown}
            aria-label={t("search.placeholder")}
            autoComplete="off"
          />
          {loading && <span className="spar-search-spinner" />}
          {query && !loading && (
            <button
              className="spar-search-clear"
              onClick={() => { setQuery(""); setOpen(false); }}
              aria-label="Clear search"
            >
              ×
            </button>
          )}
        </div>
      )}


      {open && (
        <div className="spar-search-dropdown" role="listbox">
          {results.length === 0 ? (
            <div className="spar-search-empty">{t("search.noResults")}</div>
          ) : (
            <>
              {results.map((artist) => (
                <button
                  key={artist._id}
                  className="spar-search-result"
                  onClick={() => handleSelect(artist._id)}
                  role="option"
                >
                  {artist.profileImage ? (
                    <img
                      src={fixImageUrl(artist.profileImage)}
                      alt={artist.name}
                      className="spar-search-avatar"
                    />
                  ) : (
                    <div className="spar-search-avatar spar-search-avatar-placeholder">
                      {artist.name?.[0] || "?"}
                    </div>
                  )}
                  <div className="spar-search-info">
                    <span className="spar-search-name">{artist.name}</span>
                    <span className="spar-search-meta">
                      {artist.specialty}
                      {artist.country ? ` · ${artist.country}` : ""}
                    </span>
                  </div>
                </button>
              ))}
              <a
                href={`/ArtistsList`}
                className="spar-search-view-all"
              >
                {t("search.viewAll")} →
              </a>
            </>
          )}
        </div>
      )}
    </div>
  );
}
